/**
 * PR 1 — Tienda PÚBLICA de catálogo (go.fluidsol.mx/<slug>/tienda), sin login.
 * El tenant publica qué productos son visibles; cualquiera ve la tienda con
 * precios y disponibilidad POR SUCURSAL. Aplica a las tres verticales.
 *
 * PR 2 — Flujo de compra público: browse → carrito → apartado (hold) → checkout
 * → Stripe → página de estado (recibo con código de entrega). Todo @Public, sin
 * Bearer; el tenant se resuelve por el slug/token dentro de fns SECURITY DEFINER.
 *
 * Patrón AvisoPage/booking público: pathname + fetch crudo. MOBILE-FIRST a 375px
 * (el cliente abre esto en el teléfono): grid a 2 col, carrito en bottom-sheet,
 * formulario en una columna, targets ≥40px. Sin alert() ni volcados de error:
 * cada estado tiene copy claro en español.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, CheckCircle2, Clock, ImageOff, Loader2, MapPin, MessageCircle, Minus,
  PackageSearch, Plus, ShoppingCart, Store, Trash2, X,
} from 'lucide-react';
import { API_BASE } from '../lib/api';
import {
  parseStoredCart, serializeCart, clearCartsForSlug, lineKey, optionIdsOf,
  type CartLine, type ChosenOption,
} from './storeCart';
import {
  isUnlimited, isSoldOut, unitCap, stockLabel, UNLIMITED_CAP, type Availability,
} from './availability';

// /<slug>/tienda  y  /<slug>/tienda/estado/<token>  — el slug excluye los
// prefijos reservados de otras rutas públicas.
const PATH_RE = /^\/([a-z0-9-]{1,100})\/tienda\/?$/i;
const STATUS_RE = /^\/([a-z0-9-]{1,100})\/tienda\/estado\/([^/]+)\/?$/i;

export const isPublicStoreRoute = (pathname: string): boolean =>
  PATH_RE.test(pathname) || STATUS_RE.test(pathname);

interface StoreBranch { id: string; name: string }
interface StoreInfo {
  name: string;
  logoUrl: string | null;
  primaryColor: string;
  hasPublicProducts: boolean;
  onlineOrdersEnabled: boolean;
  acceptsPrepNow: boolean;
  branches: StoreBranch[];
}
interface StoreProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  category: string | null;
  categoryId: string | null;
  trackInventory: boolean;
  availableQty: number | null;
  // TREN4 — tiene grupos de modificador publicables: "Agregar" abre el modal.
  hasModifiers: boolean;
  // PR2 (V191) — tallas/variantes comprables con disponibilidad real por talla ([] = sin desglose).
  variants: StoreVariant[];
}
// available: number | null — null = SIN RASTREO (ilimitado). Ver ./availability.
interface StoreVariant { id: string; name: string; price: number; available: Availability }
// TREN4 — grupos/opciones de un producto (GET :slug/products/:id/modifiers).
interface StoreModOption { id: string; name: string; priceFinal: number }
interface StoreModGroup {
  groupId: string; name: string; minSelect: number; maxSelect: number | null;
  required: boolean; options: StoreModOption[];
}
interface HoldLine { productId: string; qty: number }
interface HoldResponse { cartToken: string; expiresAt: string; lines: HoldLine[] }
interface CheckoutResponse { checkoutUrl: string; manageToken: string; orderId: string }
interface OrderStatus {
  deliveryCode: string;
  status: string;
  paymentStatus: string;
  fulfillmentStatus: string;
  branchName: string;
  branchPhone?: string | null; // P5 — para el botón wa.me al negocio
  total: number;
  currency: string;
  prepNow: boolean;
  // TREN4 — cada item lleva sus extras (nombre + precio con IVA) para el recibo. P4 — + nota. PR2 — + talla.
  items: { name: string; qty: number; variant?: string | null; note?: string | null; modifiers?: { name: string; price: number }[] }[];
  createdAt: string;
  /** Fidelidad v4 — null = la compra no trajo código; stamped = el sello ya se aplicó. */
  loyalty?: { stamped: boolean } | null;
}

interface ApiFail { status: number; body: any }

async function publicJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body } as ApiFail;
  return body as T;
}

/** P5 — link wa.me al negocio con folio + resumen ya escritos (el cliente lo envía).
 *  El sistema NO manda WhatsApp; solo abre el chat. Requiere teléfono con lada (≥8 díg). */
function waMeUrl(order: OrderStatus): string | null {
  const digits = (order.branchPhone ?? '').replace(/\D/g, '');
  if (digits.length < 8) return null;
  const lines = order.items.map((it) => {
    const extras = (it.modifiers ?? []).map((m) => m.name).join(', ');
    return `• ${it.qty}x ${it.name}${extras ? ` (${extras})` : ''}${it.note ? ` — "${it.note}"` : ''}`;
  }).join('\n');
  const msg =
    `Hola, tengo un pedido en ${order.branchName}.\n` +
    `Código: ${order.deliveryCode}\n${lines}\n` +
    `Total: ${money(order.total, order.currency)}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

const money = (n: number, currency: string) => {
  try {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

/** Disponibilidad legible: sin rastreo = "Disponible"; con rastreo = número o "Agotado".
 *  PR2 — un producto CON tallas muestra "Elige talla" (la disponibilidad real es por talla). */
function availability(p: StoreProduct): { label: string; tone: 'ok' | 'low' | 'out' } {
  if (p.variants.length > 0) {
    // Si alguna talla es ilimitada (sin rastreo) NUNCA está agotado. Sólo se declara
    // "Agotado" cuando TODAS se rastrean y suman 0 (null se trataría como 0 en `<=`).
    if (p.variants.some((v) => isUnlimited(v.available))) return { label: 'Elige talla', tone: 'ok' };
    const total = p.variants.reduce((s, v) => s + unitCap(v.available), 0);
    if (total <= 0) return { label: 'Agotado', tone: 'out' };
    return { label: 'Elige talla', tone: 'ok' };
  }
  if (!p.trackInventory || p.availableQty == null) return { label: 'Disponible', tone: 'ok' };
  if (p.availableQty <= 0) return { label: 'Agotado', tone: 'out' };
  if (p.availableQty <= 3) return { label: `Solo ${p.availableQty}`, tone: 'low' };
  return { label: `${p.availableQty} disponibles`, tone: 'ok' };
}

// Tope de unidades cuando se rastrea inventario. PR2 — por TALLA si se pasa la variante.
function maxFor(p: StoreProduct, variant?: StoreVariant | null): number {
  if (variant) return unitCap(variant.available); // null (ilimitado) => UNLIMITED_CAP, no 0
  if (p.variants.length > 0) {
    if (p.variants.some((v) => isUnlimited(v.available))) return UNLIMITED_CAP;
    return Math.max(0, p.variants.reduce((s, v) => s + unitCap(v.available), 0));
  }
  if (!p.trackInventory || p.availableQty == null) return UNLIMITED_CAP;
  return Math.max(0, p.availableQty);
}

// ---------------------------------------------------------------------------
// Router raíz de la tienda: catálogo/compra vs. página de estado del pedido.
// ---------------------------------------------------------------------------
export function PublicStorePage() {
  const statusMatch = window.location.pathname.match(STATUS_RE);
  if (statusMatch) {
    return <OrderStatusPage slug={statusMatch[1].toLowerCase()} token={statusMatch[2]} />;
  }
  return <StoreCatalog />;
}

// ---------------------------------------------------------------------------
// Carrito persistido (sobrevive el round-trip a Stripe y la expiración del hold)
// ---------------------------------------------------------------------------
// TREN4 — el carrito son LÍNEAS (producto + qty + opciones); ver storeCart.ts.
type Cart = CartLine[];
// Línea del carrito ya resuelta contra el producto (para pintar y cobrar en la UI).
interface CartViewLine {
  key: string;               // lineKey estable (producto + opciones)
  line: CartLine;
  product: StoreProduct;
  unitPrice: number;         // base + Σ opciones (precio final con IVA)
  lineTotal: number;         // unitPrice * qty
  atMax: boolean;            // el total del producto (todas sus líneas) tocó el techo de stock
}
const cartKey = (slug: string, branchId: string) => `ag_store_cart.${slug}.${branchId}`;

// #4 (smoke 08/09) — formato con sello (expira 24 h; el legado sin sello se
// descarta: era el carrito zombi del hallazgo). El v2 en vuelo se migra a líneas.
function loadCart(slug: string, branchId: string): { lines: Cart; fromPreviousVisit: boolean } {
  if (!branchId) return { lines: [], fromPreviousVisit: false };
  try {
    const parsed = parseStoredCart(localStorage.getItem(cartKey(slug, branchId)), Date.now());
    if (!parsed) { localStorage.removeItem(cartKey(slug, branchId)); return { lines: [], fromPreviousVisit: false }; }
    return parsed;
  } catch { return { lines: [], fromPreviousVisit: false }; }
}
function saveCart(slug: string, branchId: string, cart: Cart): void {
  if (!branchId) return;
  try {
    if (cart.length === 0) localStorage.removeItem(cartKey(slug, branchId));
    else localStorage.setItem(cartKey(slug, branchId), serializeCart(cart, Date.now()));
  } catch { /* almacenamiento no disponible: el carrito vive solo en memoria */ }
}

// Recorta el carrito al stock disponible POR PRODUCTO (reparte lo disponible entre las
// líneas del producto en orden; las que no alcanzan se caen). Un producto sin entrada en
// el mapa queda intacto. Con una sola línea por producto (sin mods) = clamp de siempre.
function clampCartToAvailable(cart: Cart, availByProduct: Map<string, number>): Cart {
  const remaining = new Map(availByProduct);
  const out: Cart = [];
  for (const line of cart) {
    if (!remaining.has(line.productId)) { out.push(line); continue; }
    const left = remaining.get(line.productId)!;
    if (left <= 0) continue;                       // agotado: la línea se cae
    const q = Math.min(line.qty, left);
    remaining.set(line.productId, left - q);
    out.push({ ...line, qty: q });
  }
  return out;
}

type Phase = 'browse' | 'checkout';

function StoreCatalog() {
  const slug = window.location.pathname.match(PATH_RE)?.[1]?.toLowerCase() ?? '';
  const [info, setInfo] = useState<StoreInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [branchId, setBranchId] = useState<string>('');
  const [products, setProducts] = useState<StoreProduct[] | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [activeCat, setActiveCat] = useState<string>('__all__');

  // Carrito + UI de compra
  const [cart, setCart] = useState<Cart>([]);
  const [cartOpen, setCartOpen] = useState(false);
  // TREN4 — producto cuyo modal de opciones está abierto (null = cerrado).
  const [modalProduct, setModalProduct] = useState<StoreProduct | null>(null);
  // #4 — N artículos restaurados de una visita anterior (0 = sin banner).
  const [prevVisitCount, setPrevVisitCount] = useState(0);
  const [phase, setPhase] = useState<Phase>('browse');

  // Apartado (hold) + checkout
  const [hold, setHold] = useState<HoldResponse | null>(null);
  const [holding, setHolding] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  // Banner de retorno de Stripe cancelado (?pago=cancelado)
  const [paymentCancelled, setPaymentCancelled] = useState(false);

  // Branding del negocio + sucursales.
  useEffect(() => {
    publicJson<StoreInfo>(`/public/store/${slug}/info`)
      .then((i) => {
        setInfo(i);
        if (i.branches.length === 1) setBranchId(i.branches[0].id); // una sola → auto
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // ?pago=cancelado → el cliente regresó de Stripe sin pagar.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('pago');
    if (p === 'cancelado') setPaymentCancelled(true);
  }, []);

  // Productos de la sucursal elegida (disponibilidad de ESA sucursal).
  useEffect(() => {
    if (!branchId) { setProducts(null); return; }
    setLoadingProducts(true);
    publicJson<StoreProduct[]>(`/public/store/${slug}/products?branchId=${branchId}`)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [slug, branchId]);

  // Al elegir sucursal, hidratar el carrito persistido de ese slug+sucursal.
  useEffect(() => {
    const restored = loadCart(slug, branchId);
    setCart(restored.lines);
    // #4 — carrito de una VISITA ANTERIOR (>30 min): banner con "Vaciar". El
    // round-trip a Stripe (minutos) NO lo dispara.
    setPrevVisitCount(restored.fromPreviousVisit
      ? restored.lines.reduce((s, l) => s + l.qty, 0) : 0);
    // Cambiar de sucursal invalida cualquier apartado/checkout en curso.
    setPhase('browse'); setHold(null); setExpired(false); setHoldError(null);
  }, [slug, branchId]);

  // Persistir el carrito ante cada cambio.
  useEffect(() => { saveCart(slug, branchId, cart); }, [slug, branchId, cart]);

  const accent = info?.primaryColor || '#0F172A';
  const canOrder = !!info?.onlineOrdersEnabled;

  const productById = useMemo(() => {
    const m = new Map<string, StoreProduct>();
    for (const p of products ?? []) m.set(p.id, p);
    return m;
  }, [products]);

  const categories = useMemo(() => {
    if (!products) return [];
    const seen = new Map<string, string>();
    for (const p of products) if (p.category) seen.set(p.category, p.category);
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [products]);

  const visible = useMemo(() => {
    if (!products) return [];
    if (activeCat === '__all__') return products;
    return products.filter((p) => p.category === activeCat);
  }, [products, activeCat]);

  // Líneas del carrito con su producto y precio (unitario = base + Σ opciones). Rule #1:
  // un producto sin modificadores es una sola línea con options=[] → unit = product.price.
  const cartLines = useMemo<CartViewLine[]>(() => {
    // PR2 — total por (producto,talla) para el tope de stock del "+": dos tallas no comparten cupo.
    const totalByPV = new Map<string, number>();
    const pvKey = (pid: string, vid?: string) => `${pid}␟${vid ?? ''}`;
    for (const l of cart) totalByPV.set(pvKey(l.productId, l.variantId), (totalByPV.get(pvKey(l.productId, l.variantId)) ?? 0) + l.qty);
    return cart
      .map((line) => {
        const product = productById.get(line.productId);
        if (!product) return null;
        const optionsTotal = line.options.reduce((s, o) => s + o.price, 0);
        // PR2 — precio base = el de la talla si el renglón la trae.
        const variant = line.variantId ? product.variants.find((v) => v.id === line.variantId) ?? null : null;
        const unitPrice = (variant ? variant.price : product.price) + optionsTotal;
        const atMax = (totalByPV.get(pvKey(line.productId, line.variantId)) ?? 0) >= maxFor(product, variant);
        return { key: lineKey(line), line, product, unitPrice, lineTotal: unitPrice * line.qty, atMax };
      })
      .filter((l): l is CartViewLine => !!l);
  }, [cart, productById]);

  const cartCount = useMemo(() => cartLines.reduce((s, l) => s + l.line.qty, 0), [cartLines]);
  const cartTotal = useMemo(() => cartLines.reduce((s, l) => s + l.lineTotal, 0), [cartLines]);
  const cartCurrency = cartLines[0]?.product.currency ?? 'MXN';

  // --- Mutadores del carrito (por LÍNEA) -------------------------------------
  // Piezas del MISMO producto (todas sus líneas) ya en el carrito, salvo `exceptKey`.
  // PR2 — el techo es por (producto[,talla]): dos tallas del mismo producto no comparten cupo.
  const qtyInCart = (c: Cart, productId: string, variantId: string | undefined, exceptKey?: string) =>
    c.reduce((s, l) => s + (l.productId === productId && l.variantId === variantId && lineKey(l) !== exceptKey ? l.qty : 0), 0);

  // Ajusta en `delta` la línea (producto+talla+opciones), respetando el techo de stock por
  // (producto,talla). Sin variantes ni modificadores hay una sola línea → idéntico a hoy.
  const bumpLine = (p: StoreProduct, variant: StoreVariant | null, options: ChosenOption[], delta: number) => {
    const key = lineKey({ productId: p.id, variantId: variant?.id, options });
    setCart((c) => {
      const idx = c.findIndex((l) => lineKey(l) === key);
      const cur = idx === -1 ? 0 : c[idx].qty;
      const headroom = Math.max(0, maxFor(p, variant) - qtyInCart(c, p.id, variant?.id, key));
      const clamped = Math.max(0, Math.min(cur + delta, headroom));
      const copy = c.slice();
      if (clamped <= 0) { if (idx !== -1) copy.splice(idx, 1); return copy; }
      const base = { productId: p.id, qty: clamped, options,
        ...(variant ? { variantId: variant.id, variantName: variant.name } : {}) };
      if (idx === -1) copy.push(base);
      else copy[idx] = { ...copy[idx], qty: clamped };
      return copy;
    });
  };

  // La talla de una línea del carrito, reconstruida desde el producto (para el techo).
  const variantOfLine = (l: CartViewLine): StoreVariant | null =>
    l.line.variantId ? (l.product.variants.find((v) => v.id === l.line.variantId) ?? null) : null;

  // "Agregar" del card: con tallas o modificadores abre el modal; sin ellos, +1 directo (rule #1).
  const onAdd = (p: StoreProduct) => { if (p.hasModifiers || p.variants.length > 0) setModalProduct(p); else bumpLine(p, null, [], 1); };
  const incLine = (l: CartViewLine) => bumpLine(l.product, variantOfLine(l), l.line.options, 1);
  const decLine = (l: CartViewLine) => bumpLine(l.product, variantOfLine(l), l.line.options, -1);
  const noModQty = (p: StoreProduct) => cart.find((l) => lineKey(l) === lineKey({ productId: p.id, options: [] }))?.qty ?? 0;
  const removeLine = (key: string) => setCart((c) => c.filter((l) => lineKey(l) !== key));
  // P4 — nota por renglón (mismo campo que order_items.notes del POS).
  const setLineNote = (key: string, note: string) =>
    setCart((c) => c.map((l) => (lineKey(l) === key ? { ...l, note: note.slice(0, 280) } : l)));
  // Confirmación del modal: mezcla/crea la línea con la talla + opciones elegidas.
  const addConfiguredLine = (p: StoreProduct, variant: StoreVariant | null, options: ChosenOption[]) => { bumpLine(p, variant, options, 1); setModalProduct(null); };

  // --- Apartado (hold) -------------------------------------------------------
  async function doHold() {
    if (cartLines.length === 0) return;
    setHolding(true); setHoldError(null); setExpired(false);
    // TREN4 — un item por LÍNEA (con sus opciones). Rule #1: un producto sin
    // modificadores manda un solo item con modifierOptionIds=[] (idéntico a hoy).
    const items = cartLines.map((l) => ({
      productId: l.product.id,
      ...(l.line.variantId ? { variantId: l.line.variantId } : {}), // PR2 — talla
      qty: l.line.qty, modifierOptionIds: optionIdsOf(l.line),
      note: l.line.note?.trim() || undefined, // P4 — nota por renglón
    }));
    try {
      const res = await publicJson<HoldResponse>(`/public/store/${slug}/hold`, {
        method: 'POST',
        body: JSON.stringify({ branchId, items }),
      });
      // Techo de stock: se agrega POR PRODUCTO (el stock es por producto, aunque haya
      // varias líneas con distintos extras). Si lo concedido no cubre lo pedido, se
      // recorta el carrito a lo disponible y se avisa; nos quedamos en el carrito.
      const granted = new Map<string, number>();
      for (const rl of res.lines) granted.set(rl.productId, (granted.get(rl.productId) ?? 0) + rl.qty);
      const requested = new Map<string, number>();
      for (const it of items) requested.set(it.productId, (requested.get(it.productId) ?? 0) + it.qty);
      const shortProducts = [...requested.entries()].filter(([pid, req]) => (granted.get(pid) ?? 0) < req);
      if (shortProducts.length > 0) {
        setCart((c) => clampCartToAvailable(c, granted));
        const names = shortProducts.map(([pid]) => productById.get(pid)?.name ?? 'un producto');
        setHoldError(`Temporalmente no disponible, intenta en unos minutos: ${names.join(', ')}.`);
        setHolding(false);
        return;
      }
      setHold(res);
      setPhase('checkout');
      setCartOpen(false);
    } catch (e) {
      handleHoldError(e as ApiFail);
    } finally {
      setHolding(false);
    }
  }

  function handleHoldError(fail: ApiFail) {
    const code = fail?.body?.code;
    if (fail.status === 409 && code === 'insufficient_stock') {
      const items: { productId: string; requested: number; available: number }[] = fail.body?.items ?? [];
      const avail = new Map(items.map((it) => [it.productId, it.available]));
      setCart((c) => clampCartToAvailable(c, avail));
      const names = items.map((it) => {
        const nm = productById.get(it.productId)?.name ?? 'un producto';
        return it.available <= 0 ? `${nm} (agotado)` : `${nm} (quedan ${it.available})`;
      });
      setHoldError(`Actualizamos tu carrito: ${names.join(', ')}.`);
      return;
    }
    if (fail.status === 409) {
      setHoldError('Los pedidos en línea no están disponibles en este momento.');
      return;
    }
    if (fail.status === 429) {
      setHoldError('Demasiados intentos, espera un momento.');
      return;
    }
    setHoldError('No pudimos apartar tu pedido. Intenta de nuevo.');
  }

  // Reintentar el apartado tras expirar (NO limpia el carrito).
  const retryHold = () => { setExpired(false); setHold(null); doHold(); };

  // --- Estados terminales honestos -------------------------------------------
  if (notFound) {
    return (
      <Shell accent="#0F172A">
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <Store className="mx-auto mb-4 h-10 w-10 text-slate-300" />
          <h1 className="text-lg font-semibold text-slate-800">Tienda no encontrada</h1>
          <p className="mt-2 text-sm text-slate-500">
            El enlace no corresponde a ninguna tienda activa. Verifica la dirección con el negocio.
          </p>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell accent="#0F172A">
        <div className="mx-auto max-w-md px-4 py-24 text-center text-sm text-slate-400">Cargando…</div>
      </Shell>
    );
  }

  return (
    <Shell accent={accent}>
      {/* Encabezado con marca del tenant */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
          {info.logoUrl
            ? <img src={info.logoUrl} alt={info.name} className="h-10 w-10 rounded-lg object-cover" />
            : <div className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ backgroundColor: accent }}>
                <Store className="h-5 w-5" />
              </div>}
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold text-slate-900">{info.name}</h1>
            <p className="text-xs text-slate-500">Catálogo en línea · recoge en sucursal</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 pb-28">
        {/* Regreso de Stripe sin pagar */}
        {paymentCancelled && (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium">El pago no se completó.</p>
              <p className="mt-0.5 text-amber-700">Puedes intentar de nuevo cuando quieras.</p>
            </div>
            <button onClick={() => setPaymentCancelled(false)} className="ml-auto -mr-1 rounded-lg p-1 text-amber-500 hover:bg-amber-100" aria-label="Cerrar">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Selector de sucursal — obligatorio antes de ver disponibilidad */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4">
          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
            <MapPin className="h-4 w-4" style={{ color: accent }} /> Elige la sucursal donde recoges
          </label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-slate-400 focus:outline-none"
          >
            <option value="">Selecciona una sucursal…</option>
            {info.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {/* Solo catálogo: pedidos en línea aún no disponibles */}
        {info.hasPublicProducts && !canOrder && (
          <p className="mb-5 rounded-xl bg-slate-100 px-4 py-3 text-center text-xs text-slate-500">
            Este negocio muestra su catálogo; los pedidos en línea no están disponibles todavía.
          </p>
        )}

        {/* Sin nada publicado: página honesta, no un error */}
        {!info.hasPublicProducts && (
          <EmptyState
            icon={<PackageSearch className="h-10 w-10 text-slate-300" />}
            title="Aún no hay productos publicados"
            body="Este negocio todavía no publica su catálogo en línea. Vuelve más tarde."
          />
        )}

        {/* Sin sucursal elegida */}
        {info.hasPublicProducts && !branchId && (
          <EmptyState
            icon={<MapPin className="h-10 w-10 text-slate-300" />}
            title="Elige una sucursal"
            body="Selecciona arriba dónde recoges para ver los productos y su disponibilidad."
          />
        )}

        {/* Catálogo */}
        {info.hasPublicProducts && branchId && (
          <>
            {categories.length > 0 && (
              <div className="mb-5 flex flex-wrap gap-2">
                <Chip active={activeCat === '__all__'} accent={accent} onClick={() => setActiveCat('__all__')}>Todo</Chip>
                {categories.map((c) => (
                  <Chip key={c} active={activeCat === c} accent={accent} onClick={() => setActiveCat(c)}>{c}</Chip>
                ))}
              </div>
            )}

            {loadingProducts && <p className="py-12 text-center text-sm text-slate-400">Cargando productos…</p>}

            {!loadingProducts && visible.length === 0 && (
              <EmptyState
                icon={<PackageSearch className="h-10 w-10 text-slate-300" />}
                title="Sin productos en esta vista"
                body="No hay productos publicados para esta sucursal o categoría."
              />
            )}

            {!loadingProducts && visible.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {visible.map((p) => {
                  const av = availability(p);
                  const soldOut = av.tone === 'out';
                  const nmQty = noModQty(p);
                  const totalQty = qtyInCart(cart, p.id, undefined);
                  return (
                    <article key={p.id} className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      {/* Hueco para foto (epic de imágenes va después): placeholder cuadrado */}
                      <div className="flex aspect-square items-center justify-center bg-slate-50">
                        <ImageOff className="h-8 w-8 text-slate-200" />
                      </div>
                      <div className="flex flex-1 flex-col p-3">
                        {p.category && <span className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">{p.category}</span>}
                        <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">{p.name}</h3>
                        {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
                        <div className="mt-auto pt-2">
                          <p className="text-base font-bold text-slate-900">{money(p.price, p.currency)}</p>
                          <span className={
                            'mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ' +
                            (av.tone === 'ok' ? 'bg-emerald-50 text-emerald-700'
                              : av.tone === 'low' ? 'bg-amber-50 text-amber-700'
                              : 'bg-slate-100 text-slate-400')
                          }>{av.label}</span>
                          {p.hasModifiers && (
                            <span className="ml-1 mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                              Personalizable
                            </span>
                          )}

                          {/* Control de compra: solo si el negocio acepta pedidos en línea */}
                          {canOrder && (
                            <div className="mt-2">
                              {soldOut ? (
                                <button disabled className="w-full cursor-not-allowed rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-400">
                                  Agotado
                                </button>
                              ) : p.hasModifiers ? (
                                // Con modificadores: SIEMPRE abre el modal (cada agregado se configura).
                                <button
                                  onClick={() => onAdd(p)}
                                  className="w-full rounded-xl px-3 py-2 text-xs font-semibold text-white active:opacity-90"
                                  style={{ backgroundColor: accent }}
                                >
                                  {totalQty > 0 ? `Agregar otro (${totalQty})` : 'Elegir opciones'}
                                </button>
                              ) : nmQty === 0 ? (
                                // Rule #1: sin modificadores, "Agregar" = +1 directo (idéntico a hoy).
                                <button
                                  onClick={() => onAdd(p)}
                                  className="w-full rounded-xl px-3 py-2 text-xs font-semibold text-white active:opacity-90"
                                  style={{ backgroundColor: accent }}
                                >
                                  Agregar
                                </button>
                              ) : (
                                <div className="flex items-center justify-between gap-1">
                                  <Stepper
                                    qty={nmQty}
                                    onDec={() => bumpLine(p, null, [], -1)}
                                    onInc={() => bumpLine(p, null, [], 1)}
                                    incDisabled={nmQty >= maxFor(p)}
                                    accent={accent}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Botón flotante del carrito (badge + total). #4: SIEMPRE visible en browse
          (aunque el carrito esté en 0) + banner de carrito de visita anterior. */}
      {canOrder && phase === 'browse' && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto max-w-5xl">
            {prevVisitCount > 0 && cartCount > 0 && (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <span>Tienes <strong>{prevVisitCount}</strong> artículo{prevVisitCount === 1 ? '' : 's'} de una visita anterior.</span>
                <button onClick={() => { setCart([]); setPrevVisitCount(0); }}
                  className="shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1 font-semibold text-amber-800 hover:bg-amber-100">
                  Vaciar
                </button>
              </div>
            )}
            <button
              onClick={() => setCartOpen(true)}
              disabled={cartCount === 0}
              className="flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-white active:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: accent }}
            >
              <span className="flex items-center gap-2">
                <span className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-slate-900">
                    {cartCount}
                  </span>
                </span>
                Ver carrito
              </span>
              <span>{money(cartTotal, cartCurrency)}</span>
            </button>
          </div>
        </div>
      )}

      {/* Bottom-sheet: carrito */}
      {cartOpen && phase === 'browse' && (
        <CartSheet
          lines={cartLines}
          total={cartTotal}
          currency={cartCurrency}
          accent={accent}
          holding={holding}
          holdError={holdError}
          onClose={() => setCartOpen(false)}
          onInc={incLine}
          onDec={decLine}
          onRemove={removeLine}
          onSetNote={setLineNote}
          onCheckout={doHold}
        />
      )}

      {/* Modal de opciones (producto con modificadores) */}
      {modalProduct && (
        <ModifierModal
          slug={slug}
          product={modalProduct}
          accent={accent}
          onClose={() => setModalProduct(null)}
          onConfirm={(variant, options) => addConfiguredLine(modalProduct, variant, options)}
        />
      )}

      {/* Bottom-sheet: formulario de checkout con cuenta regresiva */}
      {phase === 'checkout' && hold && (
        <CheckoutSheet
          slug={slug}
          info={info}
          hold={hold}
          accent={accent}
          expired={expired}
          onExpire={() => setExpired(true)}
          onRetry={retryHold}
          onClose={() => { setPhase('browse'); setHold(null); setExpired(false); }}
        />
      )}

      <footer className="mx-auto max-w-5xl px-4 py-8 text-center text-xs text-slate-400">
        Catálogo en línea · {info.name}
      </footer>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Carrito (bottom-sheet via portal)
// ---------------------------------------------------------------------------
function CartSheet({
  lines, total, currency, accent, holding, holdError, onClose, onInc, onDec, onRemove, onSetNote, onCheckout,
}: {
  lines: CartViewLine[];
  total: number; currency: string; accent: string; holding: boolean; holdError: string | null;
  onClose: () => void;
  onInc: (l: CartViewLine) => void;
  onDec: (l: CartViewLine) => void;
  onRemove: (key: string) => void;
  onSetNote: (key: string, note: string) => void;
  onCheckout: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex max-h-[85vh] flex-col rounded-t-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Tu carrito</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {lines.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Tu carrito está vacío.</p>
          ) : (
            <ul className="space-y-3">
              {lines.map((l) => (
                <li key={l.key} className="space-y-1.5">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{l.product.name}
                        {l.line.variantName && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{l.line.variantName}</span>}
                      </p>
                      {l.line.options.length > 0 && (
                        <p className="text-xs text-slate-500">{l.line.options.map((o) => o.name).join(' · ')}</p>
                      )}
                      <p className="text-xs text-slate-400">{money(l.unitPrice, l.product.currency)} c/u</p>
                    </div>
                    <Stepper
                      qty={l.line.qty}
                      onDec={() => onDec(l)}
                      onInc={() => onInc(l)}
                      incDisabled={l.atMax}
                      accent={accent}
                    />
                    <p className="w-20 text-right text-sm font-semibold text-slate-900">{money(l.lineTotal, l.product.currency)}</p>
                    <button onClick={() => onRemove(l.key)} className="rounded-lg p-2 text-slate-300 hover:bg-slate-100 hover:text-rose-500" aria-label="Quitar">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {/* P4 — nota por renglón (ej. "sin cebolla"); se manda a cocina y al recibo. */}
                  <input
                    value={l.line.note ?? ''}
                    onChange={(e) => onSetNote(l.key, e.target.value)}
                    maxLength={280}
                    placeholder="Nota (ej. sin cebolla)…"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {holdError && (
          <div className="mx-4 mb-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{holdError}</span>
          </div>
        )}

        <div className="border-t border-slate-100 px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="text-slate-500">Subtotal</span>
            <span className="text-lg font-bold text-slate-900">{money(total, currency)}</span>
          </div>
          <button
            onClick={onCheckout}
            disabled={lines.length === 0 || holding}
            className="flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 active:opacity-90"
            style={{ backgroundColor: accent }}
          >
            {holding ? <><Loader2 className="h-4 w-4 animate-spin" /> Apartando…</> : 'Proceder al pago'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Checkout (bottom-sheet via portal) — con cuenta regresiva al hold.expiresAt
// ---------------------------------------------------------------------------
function CheckoutSheet({
  slug, info, hold, accent, expired, onExpire, onRetry, onClose,
}: {
  slug: string; info: StoreInfo; hold: HoldResponse; accent: string;
  expired: boolean; onExpire: () => void; onRetry: () => void; onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [prepNow, setPrepNow] = useState(false); // default: "avísenme cuando llegue"
  const [privacy, setPrivacy] = useState(false);
  // Fidelidad v4 — código de lealtad OPCIONAL (solo código, nunca teléfono). Llega precargado
  // desde la cartilla ("Comprar en línea con mi código" → ?lealtad=CODIGO).
  const [loyaltyCode, setLoyaltyCode] = useState(() => new URLSearchParams(window.location.search).get('lealtad') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remaining = useCountdown(hold.expiresAt, onExpire);

  // P5 — correo OBLIGATORIO (para la confirmación de compra). Validación simple de formato.
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canPay = name.trim().length > 0 && phone.trim().length > 0 && emailOk && privacy && !expired && !submitting;

  async function pay() {
    if (!canPay) return;
    setSubmitting(true); setError(null);
    try {
      const res = await publicJson<CheckoutResponse>(`/public/store/${slug}/checkout`, {
        method: 'POST',
        body: JSON.stringify({
          cartToken: hold.cartToken,
          customerName: name.trim(),
          customerPhone: phone.trim(),
          customerEmail: email.trim(),
          prepNow,
          acceptedPrivacy: true,
          ...(loyaltyCode.trim() ? { loyaltyCode: loyaltyCode.trim() } : {}),
        }),
      });
      window.location.href = res.checkoutUrl; // → Stripe
    } catch (e) {
      const fail = e as ApiFail;
      const code = fail?.body?.code;
      if (fail.status === 409 && code === 'hold_expired') { setSubmitting(false); onExpire(); return; }
      if (fail.status === 400 && code === 'loyalty_code_invalid') {
        setError('Código no válido. Revísalo o déjalo vacío para pagar sin sumar sello.'); setSubmitting(false); return;
      }
      if (fail.status === 503) setError('Este negocio aún no puede aceptar pagos en línea. Intenta más tarde.');
      else if (fail.status === 429) setError('Demasiados intentos, espera un momento.');
      else setError('No pudimos iniciar el pago. Intenta de nuevo.');
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex max-h-[92vh] flex-col rounded-t-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Tus datos</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cuenta regresiva del apartado, siempre visible */}
        {!expired && (
          <div className="flex items-center justify-center gap-2 bg-slate-50 px-4 py-2 text-sm text-slate-600">
            <Clock className="h-4 w-4" style={{ color: accent }} />
            Tu apartado expira en <span className="font-mono font-semibold tabular-nums text-slate-900">{fmtMMSS(remaining)}</span>
          </div>
        )}

        {expired ? (
          <div className="px-6 py-12 text-center">
            <Clock className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <h3 className="text-base font-semibold text-slate-800">Tu apartado expiró</h3>
            <p className="mt-1.5 text-sm text-slate-500">
              Guardamos tu carrito. Vuelve a intentarlo para reservar de nuevo.
            </p>
            <button
              onClick={onRetry}
              className="mt-5 w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white active:opacity-90"
              style={{ backgroundColor: accent }}
            >
              Volver a intentar
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-3">
                <Field label="Nombre" required>
                  <input
                    value={name} onChange={(e) => setName(e.target.value)}
                    autoComplete="name" placeholder="Tu nombre"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </Field>
                <Field label="Teléfono" required>
                  <input
                    value={phone} onChange={(e) => setPhone(e.target.value)}
                    type="tel" inputMode="tel" autoComplete="tel" placeholder="10 dígitos"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </Field>
                <Field label="Correo" required>
                  <input
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    type="email" inputMode="email" autoComplete="email" placeholder="tu@correo.com"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </Field>

                {info.acceptsPrepNow && (
                  <fieldset className="rounded-xl border border-slate-200 p-3">
                    <legend className="px-1 text-xs font-medium text-slate-500">¿Cuándo lo preparamos?</legend>
                    <label className="flex items-start gap-2 py-1.5 text-sm text-slate-700">
                      <input type="radio" name="prep" checked={!prepNow} onChange={() => setPrepNow(false)} className="mt-0.5 h-4 w-4" />
                      <span>Avísenme cuando llegue</span>
                    </label>
                    <label className="flex items-start gap-2 py-1.5 text-sm text-slate-700">
                      <input type="radio" name="prep" checked={prepNow} onChange={() => setPrepNow(true)} className="mt-0.5 h-4 w-4" />
                      <span>Preparar mi pedido ahora</span>
                    </label>
                  </fieldset>
                )}

                <Field label="Código de lealtad (opcional)">
                  <input
                    value={loyaltyCode} onChange={(e) => setLoyaltyCode(e.target.value)}
                    autoComplete="off" placeholder="ABCD-2346" maxLength={20}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-sm uppercase tracking-wider focus:border-slate-400 focus:outline-none"
                  />
                  <span className="mt-1 block text-[11px] text-slate-400">Si eres miembro del programa de lealtad, tu compra suma un sello al confirmarse el pago. Las recompensas se canjean en sucursal.</span>
                </Field>

                <label className="flex items-start gap-2.5 py-1 text-sm text-slate-700">
                  <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4" />
                  <span>
                    He leído y acepto el{' '}
                    <a href={`/aviso/${slug}`} target="_blank" rel="noopener noreferrer" className="font-medium underline" style={{ color: accent }}>
                      Aviso de Privacidad
                    </a>
                  </span>
                </label>

                {error && (
                  <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-100 px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={pay}
                disabled={!canPay}
                className="flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 active:opacity-90"
                style={{ backgroundColor: accent }}
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirigiendo…</> : 'Pagar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Modal de opciones de un producto con modificadores (bottom-sheet via portal)
// ---------------------------------------------------------------------------
function ModifierModal({
  slug, product, accent, onClose, onConfirm,
}: {
  slug: string; product: StoreProduct; accent: string;
  onClose: () => void; onConfirm: (variant: StoreVariant | null, options: ChosenOption[]) => void;
}) {
  const [groups, setGroups] = useState<StoreModGroup[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // PR2 — talla elegida (obligatoria si el producto tiene variantes). Sin auto-seleccion.
  const [variantId, setVariantId] = useState<string | null>(null);
  const variant = product.variants.find((v) => v.id === variantId) ?? null;
  const needsVariant = product.variants.length > 0;
  // Si el producto NO tiene grupos de modificador, no hay nada async que cargar.
  const noMods = !product.hasModifiers;

  useEffect(() => {
    let alive = true;
    if (noMods) { setGroups([]); return; } // producto solo-tallas: nada async que cargar
    publicJson<StoreModGroup[]>(`/public/store/${slug}/products/${product.id}/modifiers`)
      .then((g) => { if (alive) setGroups(g); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [slug, product.id, noMods]);

  // Índice opción→grupo para togglear respetando el tope del grupo.
  const optionById = useMemo(() => {
    const m = new Map<string, { opt: StoreModOption; group: StoreModGroup }>();
    for (const g of groups ?? []) for (const o of g.options) m.set(o.id, { opt: o, group: g });
    return m;
  }, [groups]);

  const countInGroup = (g: StoreModGroup) => g.options.filter((o) => selected.has(o.id)).length;

  const toggle = (g: StoreModGroup, o: StoreModOption) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(o.id)) { next.delete(o.id); return next; }
      // Grupo de una sola opción (max 1): reemplaza la elegida de ESE grupo.
      if (g.maxSelect === 1) { for (const x of g.options) next.delete(x.id); next.add(o.id); return next; }
      // Multi: respeta el máximo del grupo (no deja pasar del tope).
      if (g.maxSelect != null && countInGroup(g) >= g.maxSelect) return prev;
      next.add(o.id);
      return next;
    });
  };

  // Validación de UX (min/requerido/max); el server revalida con el validador puro.
  const unmet = (groups ?? []).filter((g) => {
    const n = countInGroup(g);
    return n < g.minSelect || (g.maxSelect != null && n > g.maxSelect);
  });
  // PR2 — con tallas hay que elegir una CON existencias (o ilimitada). isSoldOut trata
  // null como ilimitado; `variant.available > 0` crudo bloqueaba la talla sin rastreo.
  const variantOk = !needsVariant || (variant != null && !isSoldOut(variant.available));
  const canConfirm = groups != null && unmet.length === 0 && variantOk;

  const extrasTotal = [...selected].reduce((s, id) => s + (optionById.get(id)?.opt.priceFinal ?? 0), 0);

  const confirm = () => {
    if (!canConfirm) return;
    const options: ChosenOption[] = [...selected]
      .map((id) => optionById.get(id))
      .filter((x): x is { opt: StoreModOption; group: StoreModGroup } => !!x)
      .map(({ opt }) => ({ id: opt.id, name: opt.name, price: opt.priceFinal }))
      .sort((a, b) => a.id.localeCompare(b.id));
    onConfirm(variant, options);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex max-h-[88vh] flex-col rounded-t-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-slate-900">{product.name}</h2>
            <p className="text-xs text-slate-500">Personaliza tu pedido</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {needsVariant && (
            <fieldset className="mb-5">
              <legend className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                Talla
                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-600">Obligatorio</span>
              </legend>
              <div className="grid grid-cols-3 gap-2">
                {product.variants.map((v) => {
                  const out = isSoldOut(v.available); // null (ilimitado) => no agotada
                  const on = variantId === v.id;
                  return (
                    <button key={v.id} type="button" disabled={out} onClick={() => setVariantId(v.id)}
                      className={'rounded-xl border px-3 py-2 text-left transition ' +
                        (on ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-800') +
                        (out ? ' cursor-not-allowed opacity-40' : ' active:bg-slate-100')}>
                      <span className="block font-medium">{v.name}</span>
                      <span className={'block text-[11px] ' + (on ? 'text-slate-300' : 'text-slate-500')}>
                        {stockLabel(v.available)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
          {loadError ? (
            <p className="py-10 text-center text-sm text-slate-400">
              No pudimos cargar las opciones. Cierra e intenta de nuevo.
            </p>
          ) : groups == null ? (
            <p className="py-10 text-center text-sm text-slate-400">Cargando opciones…</p>
          ) : (
            <div className="space-y-5">
              {groups.map((g) => {
                const n = countInGroup(g);
                const atMax = g.maxSelect != null && n >= g.maxSelect;
                return (
                  <fieldset key={g.groupId}>
                    <legend className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                      {g.name}
                      {g.required
                        ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-600">Obligatorio</span>
                        : <span className="text-[11px] font-medium text-slate-400">Opcional</span>}
                      {g.maxSelect != null && g.maxSelect > 1 && (
                        <span className="text-[11px] font-medium text-slate-400">hasta {g.maxSelect}</span>
                      )}
                    </legend>
                    <div className="space-y-1.5">
                      {g.options.map((o) => {
                        const isSel = selected.has(o.id);
                        const single = g.maxSelect === 1;
                        return (
                          <label key={o.id}
                            className={'flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm ' +
                              (isSel ? 'border-slate-300 bg-slate-50' : 'border-slate-200') +
                              (!isSel && atMax && !single ? ' opacity-50' : '')}>
                            <span className="flex items-center gap-2.5 text-slate-700">
                              <input
                                type={single ? 'radio' : 'checkbox'}
                                name={g.groupId}
                                checked={isSel}
                                onChange={() => toggle(g, o)}
                                disabled={!isSel && atMax && !single}
                                className="h-4 w-4"
                              />
                              {o.name}
                            </span>
                            {o.priceFinal > 0 && (
                              <span className="text-xs font-medium text-slate-500">+ {money(o.priceFinal, product.currency)}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-slate-500">Precio</span>
            <span className="text-lg font-bold text-slate-900">{money((variant ? variant.price : product.price) + extrasTotal, product.currency)}</span>
          </div>
          <button
            onClick={confirm}
            disabled={!canConfirm}
            className="w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 active:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Agregar al carrito
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Página de estado del pedido (/<slug>/tienda/estado/<token>) — recibo capturable
// ---------------------------------------------------------------------------
function OrderStatusPage({ slug, token }: { slug: string; token: string }) {
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pagoOk, setPagoOk] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('pago');
    if (p === 'ok') setPagoOk(true);
  }, []);

  const load = () => {
    setLoading(true);
    publicJson<OrderStatus>(`/public/store/order/${encodeURIComponent(token)}`)
      .then((o) => { setOrder(o); setNotFound(false); })
      .catch((e) => { if ((e as ApiFail).status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const accent = '#0F172A';
  const paid = order ? isPaid(order.paymentStatus) : false;

  // #4 (smoke 08/09) — pago EXITOSO: se vacían TODOS los carritos guardados de esta
  // tienda (cualquier sucursal). El cliente no puede re-pagar lo de hoy mañana.
  useEffect(() => {
    if (paid) { try { clearCartsForSlug(localStorage, slug); } catch { /* sin storage */ } }
  }, [paid, slug]);

  if (loading && !order) {
    return (
      <Shell accent={accent}>
        <div className="mx-auto max-w-md px-4 py-24 text-center text-sm text-slate-400">Cargando tu pedido…</div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell accent={accent}>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <PackageSearch className="mx-auto mb-4 h-10 w-10 text-slate-300" />
          <h1 className="text-lg font-semibold text-slate-800">No encontramos este pedido</h1>
          <p className="mt-2 text-sm text-slate-500">Revisa el enlace; puede estar incompleto o haber cambiado.</p>
        </div>
      </Shell>
    );
  }

  if (!order) return <Shell accent={accent}><div className="py-24" /></Shell>;

  return (
    <Shell accent={accent}>
      <main className="mx-auto max-w-md px-4 py-6">
        {/* Banner de éxito tras Stripe */}
        {pagoOk && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <p><span className="font-semibold">¡Gracias!</span> Tu pedido está confirmado. Muestra este código en la sucursal.</p>
          </div>
        )}

        {/* Confirmación de pago pendiente (el webhook puede tardar) */}
        {!paid && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Estamos confirmando tu pago…
            </span>
            <button onClick={load} disabled={loading} className="shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50">
              Actualizar
            </button>
          </div>
        )}

        {/* Fidelidad v4 — estado del sello (sin datos del miembro). */}
        {order.loyalty && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {order.loyalty.stamped ? 'Sello aplicado a tu cartilla de lealtad.' : 'Tu sello de lealtad se aplica al confirmarse el pago.'}
          </div>
        )}

        {/* Recibo capturable */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="px-5 py-6 text-center" style={{ backgroundColor: accent }}>
            <p className="text-xs font-medium uppercase tracking-wide text-white/70">Código de entrega</p>
            <p className="mt-1 font-mono text-4xl font-black tracking-widest text-white">{order.deliveryCode}</p>
          </div>

          <div className="space-y-4 px-5 py-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Sucursal</span>
              <span className="font-medium text-slate-900">{order.branchName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Estado</span>
              <StatusBadge order={order} paid={paid} />
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Tu pedido</p>
              <ul className="space-y-1.5">
                {order.items.map((it, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700">{it.name}</span>
                      <span className="font-medium text-slate-500">× {it.qty}</span>
                    </div>
                    {it.modifiers && it.modifiers.length > 0 && (
                      <ul className="ml-3 mt-0.5 space-y-0.5">
                        {it.modifiers.map((m, j) => (
                          <li key={j} className="text-xs text-slate-400">+ {m.name}</li>
                        ))}
                      </ul>
                    )}
                    {it.note && <p className="ml-3 mt-0.5 text-xs italic text-slate-400">“{it.note}”</p>}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <span className="text-sm text-slate-500">Total pagado</span>
              <span className="text-xl font-bold text-slate-900">{money(order.total, order.currency)}</span>
            </div>
          </div>
        </div>

        {/* P5 — "Avísale al negocio por WhatsApp" (wa.me). El sistema NO manda WhatsApp
            (decisión 24/08): el cliente le escribe al negocio con el folio y el resumen. */}
        {(() => {
          const url = waMeUrl(order);
          if (!url) return null;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white active:opacity-90">
              <MessageCircle className="h-4 w-4" /> Avísale a {order.branchName} por WhatsApp
            </a>
          );
        })()}

        <p className="mt-4 text-center text-xs text-slate-400">
          Guarda o toma captura de este código. Es tu comprobante en la sucursal.
        </p>
      </main>
    </Shell>
  );
}

function StatusBadge({ order, paid }: { order: OrderStatus; paid: boolean }) {
  let label = 'Pagado';
  let cls = 'bg-emerald-50 text-emerald-700';
  if (!paid) { label = 'Confirmando pago'; cls = 'bg-amber-50 text-amber-700'; }
  else if (isReady(order.fulfillmentStatus)) { label = 'Listo'; cls = 'bg-emerald-50 text-emerald-700'; }
  else if (isPreparing(order.fulfillmentStatus)) { label = 'En preparación'; cls = 'bg-sky-50 text-sky-700'; }
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

// ---------------------------------------------------------------------------
// Helpers de estado (tolerantes a variaciones del backend)
// ---------------------------------------------------------------------------
const isPaid = (s: string) => /paid|pagado|succeeded|complete/i.test(s ?? '');
const isReady = (s: string) => /ready|listo|fulfilled|completado|entregado/i.test(s ?? '');
const isPreparing = (s: string) => /prep|preparaci|in_progress|progreso/i.test(s ?? '');

// ---------------------------------------------------------------------------
// Cuenta regresiva
// ---------------------------------------------------------------------------
function useCountdown(expiresAt: string, onExpire: () => void): number {
  const target = useMemo(() => new Date(expiresAt).getTime(), [expiresAt]);
  const [now, setNow] = useState(() => Date.now());
  const fired = useRef(false);

  useEffect(() => {
    fired.current = false;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const remaining = Math.max(0, target - now);
  useEffect(() => {
    if (remaining <= 0 && !fired.current) { fired.current = true; onExpire(); }
  }, [remaining, onExpire]);

  return remaining;
}

function fmtMMSS(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// --- Piezas de UI ------------------------------------------------------------
function Shell({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50" style={{ ['--brand-primary' as string]: accent }}>
      <div className="h-1.5 w-full" style={{ backgroundColor: accent }} />
      {children}
    </div>
  );
}

function Chip({ active, accent, onClick, children }: {
  active: boolean; accent: string; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={'rounded-full border px-3 py-1.5 text-sm font-medium transition ' +
        (active ? 'border-transparent text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')}
      style={active ? { backgroundColor: accent } : undefined}
    >{children}</button>
  );
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-16 text-center">
      <div className="mx-auto mb-3 flex justify-center">{icon}</div>
      <h2 className="text-base font-semibold text-slate-800">{title}</h2>
      <p className="mt-1.5 text-sm text-slate-500">{body}</p>
    </div>
  );
}

// Stepper de cantidad (targets ≥40px para tap en móvil).
function Stepper({ qty, onDec, onInc, incDisabled, accent }: {
  qty: number; onDec: () => void; onInc: () => void; incDisabled: boolean; accent: string;
}) {
  return (
    <div className="flex items-center gap-1 rounded-xl border border-slate-200 p-0.5">
      <button onClick={onDec} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 active:bg-slate-100" aria-label="Quitar uno">
        <Minus className="h-4 w-4" />
      </button>
      <span className="min-w-6 text-center text-sm font-semibold tabular-nums text-slate-900">{qty}</span>
      <button
        onClick={onInc}
        disabled={incDisabled}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-white disabled:opacity-30 active:opacity-90"
        style={{ backgroundColor: accent }}
        aria-label="Agregar uno"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">
        {label}{required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
